<template>
    <div class="performance-chart-wrapper" v-if="performanceStats">
        <div class="performance-chart-container">
            <TimePeriodSelector :modelValue="selectedDays" @period-selected="handleDayChange" />
            <div v-if="isLoading" class="loading-state">
                <p>Loading<span class="ellipsis animated-ellipsis"></span></p>
            </div>
            <svg v-if="!isLoading" :id="`performance-chart-${chartId}`"></svg>
        </div>
    </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import TimePeriodSelector from './TimePeriodSelector.vue';

export default {
    name: 'PerformanceChart',
    components: {
        TimePeriodSelector,
    },
    props: {
        performanceStats: {
            type: Object,
            default: null,
        },
        performanceStatsLoaded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            chartId: uuid(),
            selectedDays: 1,
            isLoading: false,
        };
    },
    watch: {
        performanceStats: {
            handler() {
                this.isLoading = false;
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true,
        },
    },
    mounted() {
        if (this.performanceStatsLoaded && this.performanceStats) {
            this.renderChart();
        }
    },
    methods: {
        handleDayChange(days) {
            this.selectedDays = days;
            this.isLoading = true;
            this.$emit('days-changed', days);
        },
        renderChart() {
            if (!this.performanceStats) return;

            const data = this.performanceStats.data || [];
            const width = 400;
            const height = 220;
            const padding = { top: 30, right: 30, bottom: 50, left: 80 };

            const chartWidth = width - padding.left - padding.right;
            const chartHeight = height - padding.top - padding.bottom;

            // Extract skill ratings for y-axis range
            const skillRatings = data.map(d => d.skillRating).filter(s => s !== null);
            const minSkill = skillRatings.length > 0 ? Math.min(...skillRatings) : 0;
            const maxSkill = skillRatings.length > 0 ? Math.max(...skillRatings) : 100;
            const skillRange = maxSkill - minSkill || 100;

            // Determine data granularity
            const isPerMatch = data.length > 0 && data[0].timestamp !== undefined;

            // Parse time values for x-axis scale
            let timeValues;
            if (isPerMatch) {
                timeValues = data.map(d => new Date(d.timestamp).getTime());
            } else {
                timeValues = data.map(d => new Date(d.date + 'T00:00:00').getTime());
            }

            const minTime = Math.min(...timeValues);
            const maxTime = Math.max(...timeValues);
            const timeRange = maxTime - minTime || 1;

            let xScale;

            if (isPerMatch) {
				// Even spacing for daily aggregated data
                const step = chartWidth / Math.max(1, data.length - 1);
                xScale = (index) => index * step;
            } else {
				// Time-based spacing for per-match data
                xScale = (index) => {
                    const t = timeValues[index];
                    return ((t - minTime) / timeRange) * chartWidth;
                };
            }

            const yScale = (skill) => {
                if (skill === null) return chartHeight;
                return chartHeight - ((skill - minSkill) / skillRange) * chartHeight;
            };

            const svg = document.getElementById(`performance-chart-${this.chartId}`);
            if (!svg) return;

            svg.setAttribute('width', width);
            svg.setAttribute('height', height);
            svg.innerHTML = '';

            const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            background.setAttribute('width', width);
            background.setAttribute('height', height);
            background.setAttribute('fill', 'transparent');
            svg.appendChild(background);

            const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            g.setAttribute('transform', `translate(${padding.left},${padding.top})`);
            svg.appendChild(g);

            if (data.length > 0) {
                const ySteps = 5;
                for (let i = 0; i <= ySteps; i++) {
                    const y = (i / ySteps) * chartHeight;
                    const skill = maxSkill - (i / ySteps) * skillRange;

                    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                    line.setAttribute('x1', 0);
                    line.setAttribute('y1', y);
                    line.setAttribute('x2', chartWidth);
                    line.setAttribute('y2', y);
                    line.setAttribute('stroke', '#e0e0e0');
                    line.setAttribute('stroke-width', 1);
                    g.appendChild(line);

                    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    label.setAttribute('x', -10);
                    label.setAttribute('y', y + 5);
                    label.setAttribute('text-anchor', 'end');
                    label.setAttribute('font-size', '12');
                    label.setAttribute('fill', '#666');
                    label.textContent = Math.round(skill);
                    g.appendChild(label);
                }

                // Line path
                let pathD = '';
                data.forEach((d, i) => {
                    const x = xScale(i);
                    const y = yScale(d.skillRating);
                    if (i === 0) {
                        pathD += `M ${x} ${y}`;
                    } else {
                        pathD += ` L ${x} ${y}`;
                    }
                });

                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute('d', pathD);
                path.setAttribute('stroke', '#68b198');
                path.setAttribute('stroke-width', 2);
                path.setAttribute('fill', 'none');
                g.appendChild(path);

                // Data points
                data.forEach((d, i) => {
                    const x = xScale(i);
                    const y = yScale(d.skillRating);

                    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                    circle.setAttribute('cx', x);
                    circle.setAttribute('cy', y);
                    circle.setAttribute('r', 4);
                    circle.setAttribute('fill', '#68b198');
                    circle.setAttribute('stroke', 'white');
                    circle.setAttribute('stroke-width', 2);
                    g.appendChild(circle);
                });

                // X-axis labels (same logic)
                if (!isPerMatch || this.selectedDays > 1) {
                    const step = Math.ceil(data.length / 8);
                    let lastLabelX = -Infinity;

                    data.forEach((d, i) => {
                        const x = xScale(i);
                        const shouldShowLabel = i % step === 0;

                        if (i === data.length - 1 && x - lastLabelX < 50) return;

                        if (shouldShowLabel || i === data.length - 1) {
                            let labelText;

                            if (isPerMatch) {
                                const dt = new Date(d.timestamp);
                                labelText = `${dt.getDate()}/${dt.getMonth() + 1} ${dt.getHours()}:${String(dt.getMinutes()).padStart(2, '0')}`;
                            } else {
                                const [year, month, day] = d.date.split('-');
                                labelText = `${day}/${month}`;
                            }

                            const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                            label.setAttribute('x', x);
                            label.setAttribute('y', chartHeight + 20);
                            label.setAttribute('text-anchor', 'middle');
                            label.setAttribute('font-size', '12');
                            label.setAttribute('fill', '#666');
                            label.textContent = labelText;
                            g.appendChild(label);

                            lastLabelX = x;
                        }
                    });
                }
            }

            // X axis line
            const xAxis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            xAxis.setAttribute('x1', 0);
            xAxis.setAttribute('y1', chartHeight);
            xAxis.setAttribute('x2', chartWidth);
            xAxis.setAttribute('y2', chartHeight);
            xAxis.setAttribute('stroke', '#666');
            xAxis.setAttribute('stroke-width', 1);
            g.appendChild(xAxis);

            // Y axis line
            const yAxis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            yAxis.setAttribute('x1', 0);
            yAxis.setAttribute('y1', 0);
            yAxis.setAttribute('x2', 0);
            yAxis.setAttribute('y2', chartHeight);
            yAxis.setAttribute('stroke', '#666');
            yAxis.setAttribute('stroke-width', 1);
            g.appendChild(yAxis);

            const yAxisLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            yAxisLabel.setAttribute('transform', `rotate(-90)`);
            yAxisLabel.setAttribute('y', 15);
            yAxisLabel.setAttribute('x', -30 - (chartHeight / 2));
            yAxisLabel.setAttribute('dy', '1em');
            yAxisLabel.setAttribute('text-anchor', 'middle');
            yAxisLabel.setAttribute('font-size', '14');
            yAxisLabel.setAttribute('fill', '#333');
            yAxisLabel.textContent = 'Skill Rating';
            svg.appendChild(yAxisLabel);

            const xAxisLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            xAxisLabel.setAttribute('x', padding.left + (chartWidth / 2));
            xAxisLabel.setAttribute('y', height - 5);
            xAxisLabel.setAttribute('text-anchor', 'middle');
            xAxisLabel.setAttribute('font-size', '14');
            xAxisLabel.setAttribute('fill', '#333');
            xAxisLabel.textContent = 'Date';
            svg.appendChild(xAxisLabel);
        }
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .performance-chart-wrapper {
        margin-top: 10px;

        .performance-chart-container {
            width: fit-content;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            align-items: center;

            .loading-state {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 500px;
                height: 220px;
                color: $dark-blue;
                font-weight: bold;
            }
        }
    }
</style>
